import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_COMPANY_API_BASE || "/api",
});

// Response interceptor: treat { result: "NG" } as error
api.interceptors.response.use((response) => {
  const data = response.data;
  if (data && data.result === "NG") {
    const error = new Error(data.errorMessage || "API error");
    error.response = response;
    error.apiError = true;
    throw error;
  }
  return response;
});

// ── Business Domain ──
export const fetchBusinessDomains = async (companyId) => {
  const res = await api.get(`/businessDomains/company/${companyId}`);
  return res.data;
};

export const fetchBusinessDomain = async (id) => {
  const res = await api.get(`/businessDomains/${id}`);
  return res.data;
};

export const createBusinessDomain = async (data) => {
  const res = await api.post("/businessDomains", data);
  return res.data;
};

export const updateBusinessDomain = async (data) => {
  const res = await api.put("/businessDomains", data);
  return res.data;
};

export const deleteBusinessDomain = async (id) => {
  const res = await api.delete(`/businessDomains/${id}`);
  return res.data;
};

export const fetchEnterpriseDomain = async (companyId) => {
  const res = await api.get(`/enterpriseDomains/company/${companyId}`);
  return res.data;
};

// ── Customer Type ──
export const fetchCustomerTypes = async (businessDomainId) => {
  const res = await api.get(`/customerTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchCustomerType = async (id) => {
  const res = await api.get(`/customerTypes/${id}`);
  return res.data;
};

export const createCustomerType = async (data) => {
  const res = await api.post("/customerTypes", data);
  return res.data;
};

export const updateCustomerType = async (data) => {
  const res = await api.put("/customerTypes", data);
  return res.data;
};

export const deleteCustomerType = async (id) => {
  const res = await api.delete(`/customerTypes/${id}`);
  return res.data;
};

// Check if customer type is in use (for personality lock)
export const checkCustomerTypeUsed = async (customerTypeId) => {
  const res = await api.get(`/customerTypes/used/${customerTypeId}`);
  return res.data;
};

// ── Customer Classification Criteria ──
export const addCustomerCriteria = async (customerTypeId, data) => {
  const res = await api.put(`/customerTypes/classificationCriteria/${customerTypeId}`, data);
  return res.data;
};

export const deleteCustomerCriteria = async (customerTypeId, data) => {
  const res = await api.delete(`/customerTypes/classificationCriteria/${customerTypeId}`, { data });
  return res.data;
};

// ── Customer Classification Criteria Value ──
export const addCustomerCriteriaValue = async (customerTypeId, data) => {
  const res = await api.put(`/customerTypes/classificationCriteriaValue/${customerTypeId}`, data);
  return res.data;
};

export const deleteCustomerCriteriaValue = async (customerTypeId, data) => {
  const res = await api.delete(`/customerTypes/classificationCriteriaValue/${customerTypeId}`, { data });
  return res.data;
};

// ── Product Type ──
export const fetchProductTypes = async (businessDomainId) => {
  const res = await api.get(`/productTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchProductType = async (id) => {
  const res = await api.get(`/productTypes/${id}`);
  return res.data;
};

export const createProductType = async (data) => {
  const res = await api.post("/productTypes", data);
  return res.data;
};

export const updateProductType = async (data) => {
  const res = await api.put("/productTypes", data);
  return res.data;
};

export const deleteProductType = async (id) => {
  const res = await api.delete(`/productTypes/${id}`);
  return res.data;
};

// ── Product Classification Criteria ──
export const addProductCriteria = async (productTypeId, data) => {
  const res = await api.put(`/productTypes/classificationCriteria/${productTypeId}`, data);
  return res.data;
};

export const deleteProductCriteria = async (productTypeId, data) => {
  const res = await api.delete(`/productTypes/classificationCriteria/${productTypeId}`, { data });
  return res.data;
};

// ── Product Classification Criteria Value ──
export const addProductCriteriaValue = async (productTypeId, data) => {
  const res = await api.put(`/productTypes/classificationCriteriaValue/${productTypeId}`, data);
  return res.data;
};

export const deleteProductCriteriaValue = async (productTypeId, data) => {
  const res = await api.delete(`/productTypes/classificationCriteriaValue/${productTypeId}`, { data });
  return res.data;
};

// ── Product Task ──
export const fetchProductTasks = async (productTypeId) => {
  const res = await api.get(`/product_tasks/product_type/${productTypeId}`);
  return res.data;
};

export const createProductTask = async (data) => {
  const res = await api.post("/product_tasks", data);
  return res.data;
};

export const updateProductTask = async (data) => {
  const res = await api.put("/product_tasks", data);
  return res.data;
};

export const deleteProductTask = async (productTaskId) => {
  const res = await api.delete(`/product_tasks/${productTaskId}`);
  return res.data;
};

// ── Member Type ──
export const fetchMemberTypes = async (businessDomainId) => {
  const res = await api.get(`/memberTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchMemberType = async (id) => {
  const res = await api.get(`/memberTypes/memberType/${id}`);
  return res.data;
};

export const createMemberType = async (businessDomainId, data) => {
  const res = await api.post(`/memberTypes/businessDomain/${businessDomainId}`, data);
  return res.data;
};

export const updateMemberType = async (memberTypeId, data) => {
  const res = await api.put(`/memberTypes/memberType/${memberTypeId}`, data);
  return res.data;
};

export const deleteMemberType = async (memberTypeId) => {
  const res = await api.delete(`/memberTypes/memberType/${memberTypeId}`);
  return res.data;
};

// ── Member Indicator (KPI) ── separate endpoints (unlike CustomerType/ProductType)
export const addMemberIndicator = async (memberTypeId, data) => {
  const res = await api.post(`/memberTypes/memberType/${memberTypeId}/indicator`, data);
  return res.data;
};

export const updateMemberIndicator = async (memberTypeId, data) => {
  const res = await api.put(`/memberTypes/memberType/${memberTypeId}/indicator`, data);
  return res.data;
};

export const deleteMemberIndicator = async (memberTypeId, data) => {
  const res = await api.delete(`/memberTypes/memberType/${memberTypeId}/indicator`, { data });
  return res.data;
};

// ── Member Classification Criteria ──
export const addMemberCriteria = async (memberTypeId, data) => {
  const res = await api.put(`/memberTypes/classificationCriteria/${memberTypeId}`, data);
  return res.data;
};

export const deleteMemberCriteria = async (memberTypeId, data) => {
  const res = await api.delete(`/memberTypes/classificationCriteria/${memberTypeId}`, { data });
  return res.data;
};

// ── Member Classification Criteria Value ──
export const addMemberCriteriaValue = async (memberTypeId, data) => {
  const res = await api.put(`/memberTypes/classificationCriteriaValue/${memberTypeId}`, data);
  return res.data;
};

export const deleteMemberCriteriaValue = async (memberTypeId, data) => {
  const res = await api.delete(`/memberTypes/classificationCriteriaValue/${memberTypeId}`, { data });
  return res.data;
};

// ── Partner Type ──
export const fetchPartnerTypes = async (businessDomainId) => {
  const res = await api.get(`/partnerTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchPartnerType = async (id) => {
  const res = await api.get(`/partnerTypes/partnerType/${id}`);
  return res.data;
};

export const createPartnerType = async (businessDomainId, data) => {
  const res = await api.post(`/partnerTypes/businessDomain/${businessDomainId}`, data);
  return res.data;
};

export const updatePartnerType = async (partnerTypeId, data) => {
  const res = await api.put(`/partnerTypes/partnerType/${partnerTypeId}`, data);
  return res.data;
};

export const deletePartnerType = async (partnerTypeId) => {
  const res = await api.delete(`/partnerTypes/partnerType/${partnerTypeId}`);
  return res.data;
};

export const addPartnerTypeIndicator = async (partnerTypeId, data) => {
  const res = await api.post(`/partnerTypes/partnerType/${partnerTypeId}/indicator`, data);
  return res.data;
};

export const updatePartnerTypeIndicator = async (partnerTypeId, data) => {
  const res = await api.put(`/partnerTypes/partnerType/${partnerTypeId}/indicator`, data);
  return res.data;
};

export const deletePartnerTypeIndicator = async (partnerTypeId, data) => {
  const res = await api.delete(`/partnerTypes/partnerType/${partnerTypeId}/indicator`, { data });
  return res.data;
};

export const addPartnerTypeCriteria = async (partnerTypeId, data) => {
  const res = await api.put(`/partnerTypes/classificationCriteria/${partnerTypeId}`, data);
  return res.data;
};

export const deletePartnerTypeCriteria = async (partnerTypeId, data) => {
  const res = await api.delete(`/partnerTypes/classificationCriteria/${partnerTypeId}`, { data });
  return res.data;
};

export const addPartnerTypeCriteriaValue = async (partnerTypeId, data) => {
  const res = await api.put(`/partnerTypes/classificationCriteriaValue/${partnerTypeId}`, data);
  return res.data;
};

export const deletePartnerTypeCriteriaValue = async (partnerTypeId, data) => {
  const res = await api.delete(`/partnerTypes/classificationCriteriaValue/${partnerTypeId}`, { data });
  return res.data;
};

// ── Job ──
export const fetchJobs = async (businessDomainId) => {
  const res = await api.get(`/jobs/business_domain/${businessDomainId}`);
  return res.data;
};

export const fetchJob = async (id) => {
  const res = await api.get(`/jobs/${id}`);
  return res.data;
};

export const createJob = async (data) => {
  const res = await api.post("/jobs", data);
  return res.data;
};

export const updateJob = async (data) => {
  const res = await api.put("/jobs", data);
  return res.data;
};

export const deleteJob = async (id) => {
  const res = await api.delete(`/jobs/${id}`);
  return res.data;
};

export const fetchJobUsed = async (id) => {
  const res = await api.get(`/jobs/used/exceptApp/${id}`);
  return res.data;
};

// ── Task ──
export const fetchTasks = async (jobId) => {
  const res = await api.get(`/tasks/job/${jobId}`);
  return res.data;
};

export const fetchDepartmentTasks = async (departmentId) => {
  const res = await api.get(`/departments/tasks/${departmentId}`);
  return res.data;
};

export const fetchTask = async (id) => {
  const res = await api.get(`/tasks/${id}`);
  return res.data;
};

export const createTask = async (data) => {
  const res = await api.post("/tasks", data);
  return res.data;
};

export const updateTask = async (data) => {
  const res = await api.put("/tasks", data);
  return res.data;
};

export const deleteTask = async (id) => {
  const res = await api.delete(`/tasks/${id}`);
  return res.data;
};

export const fetchTaskEditable = async (id) => {
  const res = await api.get(`/tasks/editable/${id}`);
  return res.data;
};

export const checkTaskDuplicate = async (jobId, taskName) => {
  const res = await api.get(`/tasks/jobId/${jobId}/taskName/${taskName}`);
  return res.data;
};

// ── Task DataType ──
export const addTaskDataType = async (taskId, dataType) => {
  const res = await api.put(`/tasks/dataType/${taskId}`, dataType);
  return res.data;
};

export const deleteTaskDataType = async (taskId, dataType) => {
  const res = await api.delete(`/tasks/dataType/${taskId}`, { data: dataType });
  return res.data;
};

// ── Guideline ──

/**
 * タスクのガイドライン一覧取得 (guidelineTarget: "Task")
 */
export const fetchGuidelines = async (taskId) => {
  const res = await api.get(`/guidelines/${taskId}`);
  return res.data;
};

/**
 * アクションのガイドライン一覧取得 (guidelineTarget: "Action")
 * GET /guidelines/{actionId}
 */
export const fetchActionGuidelines = async (actionId) => {
  const res = await api.get(`/guidelines/${actionId}`);
  return res.data;
};

export const createGuideline = async (data) => {
  const res = await api.post("/guidelines", data);
  return res.data;
};

export const updateGuideline = async (data) => {
  const res = await api.put("/guidelines", data);
  return res.data;
};

export const deleteGuideline = async (guidelineId) => {
  const res = await api.delete(`/guidelines/${guidelineId}`);
  return res.data;
};

// ── Agent (guidelineTarget: "Agent") ──
// Create/Update/Delete reuse /guidelines endpoints (same API, different guidelineTarget)
export const fetchAgents = async (taskId) => {
  const res = await api.get(`/agents/${taskId}`);
  return res.data;
};

// ── Business Process Task ──
export const addBusinessProcessTask = async (businessProcessId, task) => {
  const res = await api.put(`/businessProcesses/task/businessProcessId/${businessProcessId}`, task);
  return res.data;
};

export const updateBusinessProcessTaskOrder = async (businessProcessId, taskId, sourceOrder, targetOrder) => {
  const res = await api.put(
    `/businessProcesses/${businessProcessId}/sourceTaskId/${taskId}/sourceTaskOrder/${sourceOrder}/targetTaskId/${taskId}/targetTaskOrder/${targetOrder}`
  );
  return res.data;
};

export const deleteBusinessProcessTask = async (businessProcessId, task) => {
  const res = await api.put(`/task/businessProcesses/businessProcessId/${businessProcessId}`, task);
  return res.data;
};

// ── Business Process ──
export const fetchBusinessProcesses = async (businessDomainId) => {
  // 旧アプリの正しいエンドポイント: /notValueChain/ (バリューチェーン以外のBPを取得)
  const res = await api.get(`/businessProcesses/notValueChain/${businessDomainId}`);
  return res.data;
};

export const fetchBusinessProcess = async (id) => {
  const res = await api.get(`/businessProcesses/${id}`);
  return res.data;
};

export const createBusinessProcess = async (data) => {
  const res = await api.post("/businessProcesses", data);
  return res.data;
};

export const updateBusinessProcess = async (data) => {
  const res = await api.put("/businessProcesses", data);
  return res.data;
};

export const deleteBusinessProcess = async (id) => {
  const res = await api.delete(`/businessProcesses/${id}`);
  return res.data;
};

// ── Value Chain ──
export const fetchValueChains = async (businessDomainId) => {
  const res = await api.get(`/businessProcesses/valueChain/${businessDomainId}`);
  return res.data;
};

export const addValueChainProcess = async (valueChainId, businessProcessId) => {
  const res = await api.put(`/businessProcesses/${valueChainId}/add/${businessProcessId}`);
  return res.data;
};

export const removeValueChainProcess = async (valueChainId, businessProcessId) => {
  const res = await api.delete(`/businessProcesses/${valueChainId}/release/${businessProcessId}`);
  return res.data;
};

// ── Data Type ──
export const fetchDataIndicators = async () => {
  const res = await api.get("/indicators/Data/JP");
  return res.data;
};

export const fetchDataTypes = async (businessDomainId) => {
  const res = await api.get(`/dataTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchDataType = async (id) => {
  const res = await api.get(`/dataTypes/${id}`);
  return res.data;
};

export const createDataType = async (data) => {
  const res = await api.post("/dataTypes", data);
  return res.data;
};

export const updateDataType = async (data) => {
  const res = await api.put("/dataTypes", data);
  return res.data;
};

export const deleteDataType = async (id) => {
  const res = await api.delete(`/dataTypes/${id}`);
  return res.data;
};
export const addDataTypeIndicator = async (id, data) => {
  const res = await api.put(`/dataTypes/indicator/add/${id}`, data);
  return res.data;
};
export const replaceDataTypeIndicator = async (id, data) => {
  const res = await api.put(`/dataTypes/indicator/replace/${id}`, data);
  return res.data;
};
export const removeDataTypeIndicator = async (id, data) => {
  const res = await api.put(`/dataTypes/indicator/remove/${id}`, data);
  return res.data;
};
export const addDataTypeCriteria = async (id, data) => {
  const res = await api.put(`/dataTypes/classificationCriteria/${id}`, data);
  return res.data;
};
export const deleteDataTypeCriteria = async (id, data) => {
  const res = await api.delete(`/dataTypes/classificationCriteria/${id}`, { data });
  return res.data;
};
export const addDataTypeCriteriaValue = async (id, data) => {
  const res = await api.put(`/dataTypes/classificationCriteriaValue/${id}`, data);
  return res.data;
};
export const deleteDataTypeCriteriaValue = async (id, data) => {
  const res = await api.delete(`/dataTypes/classificationCriteriaValue/${id}`, { data });
  return res.data;
};

// ── Application Type ──
export const fetchApplicationTypes = async (businessDomainId) => {
  const res = await api.get(`/applicationTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchApplicationType = async (id) => {
  const res = await api.get(`/applicationTypes/${id}`);
  return res.data;
};

export const createApplicationType = async (data) => {
  const res = await api.post("/applicationTypes", data);
  return res.data;
};

export const updateApplicationType = async (data) => {
  const res = await api.put("/applicationTypes", data);
  return res.data;
};

export const deleteApplicationType = async (id) => {
  const res = await api.delete(`/applicationTypes/${id}`);
  return res.data;
};
export const addApplicationTypeCriteria = async (id, data) => {
  const res = await api.put(`/applicationTypes/classificationCriteria/${id}`, data);
  return res.data;
};
export const deleteApplicationTypeCriteria = async (id, data) => {
  const res = await api.delete(`/applicationTypes/classificationCriteria/${id}`, { data });
  return res.data;
};
export const addApplicationTypeCriteriaValue = async (id, data) => {
  const res = await api.put(`/applicationTypes/classificationCriteriaValue/${id}`, data);
  return res.data;
};
export const deleteApplicationTypeCriteriaValue = async (id, data) => {
  const res = await api.delete(`/applicationTypes/classificationCriteriaValue/${id}`, { data });
  return res.data;
};
// Application Tasks
export const fetchApplicationTasks = async (applicationTypeId) => {
  const res = await api.get(`/applicationTasks/applicationType/${applicationTypeId}`);
  return res.data;
};

export const fetchApplicationTask = async (applicationTaskId) => {
  const res = await api.get(`/applicationTasks/${applicationTaskId}`);
  return res.data;
};
export const createApplicationTask = async (data) => {
  const res = await api.post("/applicationTasks", data);
  return res.data;
};
export const deleteApplicationTask = async (id) => {
  const res = await api.delete(`/applicationTasks/${id}`);
  return res.data;
};

// ── IT Type ──
export const fetchItTypes = async (businessDomainId) => {
  const res = await api.get(`/itTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchItType = async (id) => {
  const res = await api.get(`/itTypes/${id}`);
  return res.data;
};

export const createItType = async (data) => {
  const res = await api.post("/itTypes", data);
  return res.data;
};

export const updateItType = async (data) => {
  const res = await api.put("/itTypes", data);
  return res.data;
};

export const deleteItType = async (id) => {
  const res = await api.delete(`/itTypes/${id}`);
  return res.data;
};
export const addItTypeCriteria = async (id, data) => {
  const res = await api.put(`/itTypes/classificationCriteria/${id}`, data);
  return res.data;
};
export const deleteItTypeCriteria = async (id, data) => {
  const res = await api.delete(`/itTypes/classificationCriteria/${id}`, { data });
  return res.data;
};
export const addItTypeCriteriaValue = async (id, data) => {
  const res = await api.put(`/itTypes/classificationCriteriaValue/${id}`, data);
  return res.data;
};
export const deleteItTypeCriteriaValue = async (id, data) => {
  const res = await api.delete(`/itTypes/classificationCriteriaValue/${id}`, { data });
  return res.data;
};

// ── Place Type ──
export const fetchPlaceTypes = async (businessDomainId) => {
  const res = await api.get(`/placeTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchPlaceType = async (id) => {
  const res = await api.get(`/placeTypes/${id}`);
  return res.data;
};

export const createPlaceType = async (data) => {
  const res = await api.post("/placeTypes", data);
  return res.data;
};

export const updatePlaceType = async (data) => {
  const res = await api.put("/placeTypes", data);
  return res.data;
};

export const deletePlaceType = async (id) => {
  const res = await api.delete(`/placeTypes/${id}`);
  return res.data;
};

export const addPlaceTypeCriteria = async (id, data) => {
  const res = await api.put(`/placeTypes/classificationCriteria/${id}`, data);
  return res.data;
};

export const deletePlaceTypeCriteria = async (id, data) => {
  const res = await api.delete(`/placeTypes/classificationCriteria/${id}`, { data });
  return res.data;
};

export const addPlaceTypeCriteriaValue = async (id, data) => {
  const res = await api.put(`/placeTypes/classificationCriteriaValue/${id}`, data);
  return res.data;
};

export const deletePlaceTypeCriteriaValue = async (id, data) => {
  const res = await api.delete(`/placeTypes/classificationCriteriaValue/${id}`, { data });
  return res.data;
};

// ── Financial Asset Type ──
export const fetchFinancialAssetTypes = async (businessDomainId) => {
  const res = await api.get(`/financialAssetTypes/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchFinancialAssetType = async (id) => {
  const res = await api.get(`/financialAssetTypes/${id}`);
  return res.data;
};

export const createFinancialAssetType = async (data) => {
  const res = await api.post("/financialAssetTypes", data);
  return res.data;
};

export const updateFinancialAssetType = async (data) => {
  const res = await api.put("/financialAssetTypes", data);
  return res.data;
};

export const deleteFinancialAssetType = async (id) => {
  const res = await api.delete(`/financialAssetTypes/${id}`);
  return res.data;
};

export const addFinancialAssetTypeCriteria = async (id, data) => {
  const res = await api.put(`/financialAssetTypes/classificationCriteria/${id}`, data);
  return res.data;
};

export const deleteFinancialAssetTypeCriteria = async (id, data) => {
  const res = await api.delete(`/financialAssetTypes/classificationCriteria/${id}`, { data });
  return res.data;
};

export const addFinancialAssetTypeCriteriaValue = async (id, data) => {
  const res = await api.put(`/financialAssetTypes/classificationCriteriaValue/${id}`, data);
  return res.data;
};

export const deleteFinancialAssetTypeCriteriaValue = async (id, data) => {
  const res = await api.delete(`/financialAssetTypes/classificationCriteriaValue/${id}`, { data });
  return res.data;
};

// =====================================================
// ── Strategy Section ──
// =====================================================

// ── Success Factors (共通) ──
export const fetchSuccessFactor = async (id) => {
  const res = await api.get(`/successFactor/${id}`);
  return res.data;
};

export const deleteSuccessFactor = async (id) => {
  const res = await api.delete(`/successFactor/${id}`);
  return res.data;
};

// ── Financial Goal ──
export const fetchFinancialGoals = async (businessDomainId) => {
  const res = await api.get(`/financialGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createFinancialGoal = async (data) => {
  const res = await api.post("/financialGoal/", data);
  return res.data;
};

export const updateFinancialGoal = async (data) => {
  const res = await api.put("/financialGoal/", data);
  return res.data;
};

// ── Customer Value ──
export const fetchCustomerValues = async (businessDomainId) => {
  const res = await api.get(`/customerValue/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createCustomerValue = async (data) => {
  const res = await api.post("/customerValue/", data);
  return res.data;
};

export const updateCustomerValue = async (data) => {
  const res = await api.put("/customerValue/", data);
  return res.data;
};

// ── Strategic Goal ──
export const fetchStrategicGoals = async (businessDomainId) => {
  const res = await api.get(`/strategicGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createStrategicGoal = async (data) => {
  const res = await api.post("/strategicGoal/", data);
  return res.data;
};

export const updateStrategicGoal = async (data) => {
  const res = await api.put("/strategicGoal/", data);
  return res.data;
};

// ── Strategic Issue ──
export const fetchStrategicIssues = async (businessDomainId) => {
  const res = await api.get(`/strategicIssue/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createStrategicIssue = async (data) => {
  const res = await api.post("/strategicIssue/", data);
  return res.data;
};

export const updateStrategicIssue = async (data) => {
  const res = await api.put("/strategicIssue/", data);
  return res.data;
};

// ── Human Capital Goal ──
export const fetchHumanCapitalGoals = async (businessDomainId) => {
  const res = await api.get(`/humanCapitalGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createHumanCapitalGoal = async (data) => {
  const res = await api.post("/humanCapitalGoal/", data);
  return res.data;
};

export const updateHumanCapitalGoal = async (data) => {
  const res = await api.put("/humanCapitalGoal/", data);
  return res.data;
};

// ── Human Capital Issue ──
export const fetchHumanCapitalIssues = async (businessDomainId) => {
  const res = await api.get(`/humanCapitalIssue/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createHumanCapitalIssue = async (data) => {
  const res = await api.post("/humanCapitalIssue/", data);
  return res.data;
};

export const updateHumanCapitalIssue = async (data) => {
  const res = await api.put("/humanCapitalIssue/", data);
  return res.data;
};

// ── Information Capital Goal (App) ──
export const fetchInfoCapitalGoalsApp = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/app/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createInformationCapitalGoal = async (data) => {
  const res = await api.post("/informationCapitalGoal/", data);
  return res.data;
};

export const updateInformationCapitalGoal = async (data) => {
  const res = await api.put("/informationCapitalGoal/", data);
  return res.data;
};

// ── Information Capital Goal (IT) ──
export const fetchInfoCapitalGoalsIt = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/it/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Information Capital Goal (Data) ──
export const fetchInfoCapitalGoalsData = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/data/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Information Capital Issue (App) ──
export const fetchInfoCapitalIssuesApp = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalIssue/app/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createInformationCapitalIssue = async (data) => {
  const res = await api.post("/informationCapitalIssue/", data);
  return res.data;
};

export const updateInformationCapitalIssue = async (data) => {
  const res = await api.put("/informationCapitalIssue/", data);
  return res.data;
};

// ── Information Capital Issue (IT) ──
export const fetchInfoCapitalIssuesIt = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalIssue/it/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Information Capital Issue (Data) ──
export const fetchInfoCapitalIssuesData = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalIssue/data/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Organization Capital Goal ──
export const fetchOrgCapitalGoals = async (businessDomainId) => {
  const res = await api.get(`/organizationCapitalGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createOrganizationCapitalGoal = async (data) => {
  const res = await api.post("/organizationCapitalGoal/", data);
  return res.data;
};

export const updateOrganizationCapitalGoal = async (data) => {
  const res = await api.put("/organizationCapitalGoal/", data);
  return res.data;
};

// ── Organization Capital Issue ──
export const fetchOrgCapitalIssues = async (businessDomainId) => {
  const res = await api.get(`/organizationCapitalIssue/businessDomain/${businessDomainId}`);
  return res.data;
};

export const createOrganizationCapitalIssue = async (data) => {
  const res = await api.post("/organizationCapitalIssue/", data);
  return res.data;
};

export const updateOrganizationCapitalIssue = async (data) => {
  const res = await api.put("/organizationCapitalIssue/", data);
  return res.data;
};

// ── Customer Category ──
export const fetchCustomerCategories = async (businessUnitId) => {
  const res = await api.get(`/customerCategories/businessUnit/${businessUnitId}`);
  return res.data;
};

export const fetchCustomerCategory = async (id) => {
  const res = await api.get(`/customerCategories/${id}`);
  return res.data;
};

export const createCustomerCategory = async (data) => {
  const res = await api.post("/customerCategories", data);
  return res.data;
};

export const updateCustomerCategory = async (data) => {
  const res = await api.put("/customerCategories", data);
  return res.data;
};

export const deleteCustomerCategory = async (id) => {
  const res = await api.delete(`/customerCategories/${id}`);
  return res.data;
};

// ── Product Category ──
export const fetchProductCategories = async (businessUnitId) => {
  const res = await api.get(`/productCategories/businessUnit/${businessUnitId}`);
  return res.data;
};

export const fetchProductCategory = async (id) => {
  const res = await api.get(`/productCategories/${id}`);
  return res.data;
};

export const createProductCategory = async (data) => {
  const res = await api.post("/productCategories", data);
  return res.data;
};

export const updateProductCategory = async (data) => {
  const res = await api.put("/productCategories", data);
  return res.data;
};

export const deleteProductCategory = async (id) => {
  const res = await api.delete(`/productCategories/${id}`);
  return res.data;
};

// ProductCategory classification criteria value
export const addProductCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/productCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteProductCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/productCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// CustomerCategory ClassificationCriteriaValue
export const addCustomerCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/customerCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteCustomerCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/customerCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// MemberCategory ClassificationCriteriaValue
export const addMemberCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/memberCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};
export const deleteMemberCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/memberCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// ProductFunction
export const fetchProductFunctions = async (categoryId) => {
  const res = await api.get(`/productFunctions/product_category/${categoryId}`);
  return res.data;
};

export const createProductFunction = async (data) => {
  const res = await api.post("/productFunctions", data);
  return res.data;
};

export const updateProductFunction = async (data) => {
  const res = await api.put("/productFunctions", data);
  return res.data;
};

export const deleteProductFunction = async (id) => {
  const res = await api.delete(`/productFunctions/${id}`);
  return res.data;
};

// ── Member Category ──
export const fetchMemberCategories = async (companyId) => {
  const res = await api.get(`/memberCategories/company/${companyId}`);
  return res.data;
};

export const fetchMemberCategory = async (id) => {
  const res = await api.get(`/memberCategories/${id}`);
  return res.data;
};

export const createMemberCategory = async (data) => {
  const res = await api.post("/memberCategories", data);
  return res.data;
};

export const updateMemberCategory = async (id, data) => {
  const res = await api.put(`/memberCategories/${id}`, data);
  return res.data;
};

export const deleteMemberCategory = async (id) => {
  const res = await api.delete(`/memberCategories/${id}`);
  return res.data;
};

// ── Partner Category ──
export const fetchPartnerCategories = async (companyId) => {
  const res = await api.get(`/partnerCategories/company/${companyId}`);
  return res.data;
};

export const fetchPartnerCategory = async (id) => {
  const res = await api.get(`/partnerCategories/${id}`);
  return res.data;
};

export const createPartnerCategory = async (data) => {
  const res = await api.post("/partnerCategories", data);
  return res.data;
};

export const updatePartnerCategory = async (id, data) => {
  const res = await api.put(`/partnerCategories/${id}`, data);
  return res.data;
};

export const deletePartnerCategory = async (id) => {
  const res = await api.delete(`/partnerCategories/${id}`);
  return res.data;
};

export const addPartnerCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/partnerCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deletePartnerCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/partnerCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

export const fetchPartnerFunctionsByCategory = async (partnerCategoryId) => {
  const res = await api.get(`/partnerFunctions/partnerCategory/${partnerCategoryId}`);
  return res.data;
};

export const fetchPartnerFunction = async (id) => {
  const res = await api.get(`/partnerFunctions/${id}`);
  return res.data;
};

export const createPartnerFunction = async (data) => {
  const res = await api.post("/partnerFunctions", data);
  return res.data;
};

export const updatePartnerFunction = async (data) => {
  const res = await api.put("/partnerFunctions", data);
  return res.data;
};

export const deletePartnerFunction = async (id) => {
  const res = await api.delete(`/partnerFunctions/${id}`);
  return res.data;
};

// ── Data Category ──
export const fetchDataCategories = async (companyId) => {
  const res = await api.get(`/dataCategories/company/${companyId}`);
  return res.data;
};

export const fetchDataCategory = async (id) => {
  const res = await api.get(`/dataCategories/${id}`);
  return res.data;
};

export const createDataCategory = async (data) => {
  const res = await api.post("/dataCategories", data);
  return res.data;
};

export const updateDataCategory = async (id, data) => {
  const res = await api.put(`/dataCategories/${id}`, data);
  return res.data;
};

export const deleteDataCategory = async (id) => {
  const res = await api.delete(`/dataCategories/${id}`);
  return res.data;
};

export const addDataCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/dataCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteDataCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/dataCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// ── Application Category ──
export const fetchApplicationCategories = async (companyId) => {
  const res = await api.get(`/applicationCategories/company/${companyId}`);
  return res.data;
};

export const fetchApplicationCategory = async (id) => {
  const res = await api.get(`/applicationCategories/${id}`);
  return res.data;
};

export const createApplicationCategory = async (data) => {
  const res = await api.post("/applicationCategories", data);
  return res.data;
};

export const updateApplicationCategory = async (id, data) => {
  const res = await api.put(`/applicationCategories/${id}`, data);
  return res.data;
};

export const deleteApplicationCategory = async (id) => {
  const res = await api.delete(`/applicationCategories/${id}`);
  return res.data;
};

export const addApplicationCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/applicationCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteApplicationCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/applicationCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

export const fetchApplicationFunctions = async (categoryId) => {
  const res = await api.get(`/applicationFunctions/applicationCategory/${categoryId}`);
  return res.data;
};

export const fetchApplicationFunction = async (id) => {
  const res = await api.get(`/applicationFunctions/${id}`);
  return res.data;
};

export const createApplicationFunction = async (data) => {
  const res = await api.post("/applicationFunctions", data);
  return res.data;
};

export const updateApplicationFunction = async (data) => {
  const res = await api.put("/applicationFunctions", data);
  return res.data;
};

export const deleteApplicationFunction = async (id) => {
  const res = await api.delete(`/applicationFunctions/${id}`);
  return res.data;
};

export const fetchDataCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/dataCategories/businessUnit/${businessUnitId}`);
  return res.data;
};

export const addApplicationCategoryDataCategory = async (categoryId, dataCategoryId) => {
  const res = await api.put(`/applicationCategories/dataCategories/add/${categoryId}/${dataCategoryId}`);
  return res.data;
};

export const removeApplicationCategoryDataCategory = async (categoryId, dataCategoryId) => {
  const res = await api.put(`/applicationCategories/dataCategories/remove/${categoryId}/${dataCategoryId}`);
  return res.data;
};

// ── IT Category ──
export const fetchItCategories = async (companyId) => {
  const res = await api.get(`/itCategories/company/${companyId}`);
  return res.data;
};

export const fetchItCategory = async (id) => {
  const res = await api.get(`/itCategories/${id}`);
  return res.data;
};

export const createItCategory = async (data) => {
  const res = await api.post("/itCategories", data);
  return res.data;
};

export const updateItCategory = async (id, data) => {
  const res = await api.put(`/itCategories/${id}`, data);
  return res.data;
};

export const deleteItCategory = async (id) => {
  const res = await api.delete(`/itCategories/${id}`);
  return res.data;
};

export const addItCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/itCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteItCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/itCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

export const addItCategoryApplicationCategory = async (itCategoryId, applicationCategoryId) => {
  const res = await api.put(`/itCategories/applicationCategories/add/${itCategoryId}/${applicationCategoryId}`);
  return res.data;
};

export const removeItCategoryApplicationCategory = async (itCategoryId, applicationCategoryId) => {
  const res = await api.put(`/itCategories/applicationCategories/remove/${itCategoryId}/${applicationCategoryId}`);
  return res.data;
};

// ── Place Category ──
export const fetchPlaceCategories = async (companyId) => {
  const res = await api.get(`/placeCategories/company/${companyId}`);
  return res.data;
};

export const fetchPlaceCategory = async (id) => {
  const res = await api.get(`/placeCategories/${id}`);
  return res.data;
};

export const createPlaceCategory = async (data) => {
  const res = await api.post("/placeCategories", data);
  return res.data;
};

export const updatePlaceCategory = async (id, data) => {
  const res = await api.put(`/placeCategories/${id}`, data);
  return res.data;
};

export const deletePlaceCategory = async (id) => {
  const res = await api.delete(`/placeCategories/${id}`);
  return res.data;
};

export const addPlaceCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/placeCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deletePlaceCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/placeCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// ── Financial Asset Category ──
export const fetchFinancialAssetCategories = async (companyId) => {
  const res = await api.get(`/financialAssetCategories/company/${companyId}`);
  return res.data;
};

export const fetchFinancialAssetCategory = async (id) => {
  const res = await api.get(`/financialAssetCategories/${id}`);
  return res.data;
};

export const createFinancialAssetCategory = async (data) => {
  const res = await api.post("/financialAssetCategories", data);
  return res.data;
};

export const updateFinancialAssetCategory = async (id, data) => {
  const res = await api.put(`/financialAssetCategories/${id}`, data);
  return res.data;
};

export const deleteFinancialAssetCategory = async (id) => {
  const res = await api.delete(`/financialAssetCategories/${id}`);
  return res.data;
};

export const addFinancialAssetCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.put(`/financialAssetCategories/classificationCriteriaValue/${categoryId}`, data);
  return res.data;
};

export const deleteFinancialAssetCategoryCriteriaValue = async (categoryId, data) => {
  const res = await api.delete(`/financialAssetCategories/classificationCriteriaValue/${categoryId}`, { data });
  return res.data;
};

// ── Department ──
export const fetchDepartments = async (companyId) => {
  const res = await api.get(`/departments/company/${companyId}`);
  return res.data;
};

export const fetchDepartmentsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/departments/business_unit/${businessUnitId}`);
  return res.data;
};

export const fetchDepartment = async (id) => {
  const res = await api.get(`/departments/${id}`);
  return res.data;
};

export const createDepartment = async (data) => {
  const res = await api.post("/departments", data);
  return res.data;
};

export const updateDepartment = async (data) => {
  const res = await api.put("/departments", data);
  return res.data;
};

export const deleteDepartment = async (id) => {
  const res = await api.delete(`/departments/${id}`);
  return res.data;
};

export const fetchJobDepartments = async (jobId) => {
  const res = await api.get(`/departments/job/${jobId}`);
  return res.data;
};

export const assignApplicationToDepartment = async (departmentId, data) => {
  const res = await api.put(`/departments/application/${departmentId}`, data);
  return res.data;
};

export const releaseApplicationFromDepartment = async (departmentId, data) => {
  const res = await api.delete(`/departments/application/${departmentId}`, { data });
  return res.data;
};

export const assignPartnerToDepartment = async (departmentId, data) => {
  const res = await api.put(`/departments/partner/${departmentId}`, data);
  return res.data;
};

export const releasePartnerFromDepartment = async (departmentId, data) => {
  const res = await api.delete(`/departments/partner/${departmentId}`, { data });
  return res.data;
};

// ── Member (Category Member) ──
export const fetchMembersByMemberCategory = async (memberCategoryId) => {
  const res = await api.get(`/members/memberCategory/${memberCategoryId}`);
  return res.data;
};

export const createMember = async (data) => {
  const res = await api.post("/members", data);
  return res.data;
};

export const updateMember = async (data) => {
  const res = await api.put("/members", data);
  return res.data;
};

export const deleteMember = async (memberId) => {
  const res = await api.delete(`/members/${memberId}`);
  return res.data;
};

// ── Department Member ──
export const fetchMembersByDepartment = async (departmentId) => {
  const res = await api.get(`/members/department/${departmentId}`);
  return res.data;
};

export const fetchAssignableMembersForDepartment = async (departmentId) => {
  const res = await api.get(`/assignable_members/department/${departmentId}`);
  return res.data;
};

export const assignMemberToDepartment = async (departmentId, companyId, data) => {
  const res = await api.put(`/assign/member/department/${departmentId}/company/${companyId}`, data);
  return res.data;
};

export const releaseMemberFromDepartment = async (memberId) => {
  const res = await api.delete(`/members/department/release/${memberId}`);
  return res.data;
};

export const fetchAssignableApplicationsForDepartment = async (departmentId) => {
  const res = await api.get(`/departments/application/${departmentId}`);
  return res.data;
};

export const fetchAssignablePartnersForDepartment = async (companyId) => {
  const res = await api.get(`/partnerCategories/company/${companyId}`);
  return res.data;
};

export const fetchMovableCompanyMembers = async (departmentId) => {
  const res = await api.get(`/members/other/department/${departmentId}`);
  return res.data;
};

export const fetchMovableMarketMembers = async (departmentId) => {
  const res = await api.get(`/members/market/other/department/${departmentId}`);
  return res.data;
};

export const fetchMarketMembersByRolesAndIndustry = async (industryId, roles) => {
  const body = roles.map((r) => ({ id: Number(r.roleId) }));
  const res = await api.post(`/members/roles/industry/${industryId}`, body);
  return res.data;
};

export const moveMemberToDepartment = async (departmentId, companyId, data) => {
  const res = await api.put(`/assign/member/other/department/${departmentId}/company/${companyId}`, data);
  return res.data;
};

export const assignMarketMemberToDepartment = async (departmentId, companyId, data) => {
  const res = await api.post(`/assign/member/department/${departmentId}/company/${companyId}`, data);
  return res.data;
};

// ── Department Action ──
export const fetchActionsByDepartment = async (departmentId) => {
  const res = await api.get(`/actions/department/${departmentId}`);
  return res.data;
};

export const fetchAction = async (actionId) => {
  const res = await api.get(`/actions/${actionId}`);
  return res.data;
};

export const createAction = async (data) => {
  const res = await api.post("/actions", data);
  return res.data;
};

export const updateAction = async (data) => {
  const res = await api.put("/actions", data);
  return res.data;
};

export const deleteAction = async (id) => {
  const res = await api.delete(`/actions/${id}`);
  return res.data;
};

// ── Action Plan ──
export const fetchActionPlans = async (companyId) => {
  const res = await api.get(`/actionPlans/company/${companyId}`);
  return res.data;
};

export const fetchActionPlansByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/actionPlans/businessUnit/${businessUnitId}`);
  return res.data;
};

export const fetchActionPlansByProject = async (projectId) => {
  const res = await api.get(`/actionPlans/project/${projectId}`);
  return res.data;
};

export const fetchActionPlan = async (id) => {
  const res = await api.get(`/actionPlans/${id}`);
  return res.data;
};

export const createActionPlan = async (data) => {
  const res = await api.post("/actionPlans", data);
  return res.data;
};

export const updateActionPlan = async (data) => {
  const res = await api.put("/actionPlans", data);
  return res.data;
};

export const deleteActionPlan = async (id) => {
  const res = await api.delete(`/actionPlans/${id}`);
  return res.data;
};

export const fetchActionsForActionPlan = async (actionPlanId) => {
  const res = await api.get(`/actions/actionPlan/${actionPlanId}`);
  return res.data;
};

export const addActionToActionPlan = async (actionPlanId, action) => {
  const res = await api.put(`/actionPlans/action/actionPlanId/${actionPlanId}`, action);
  return res.data;
};

export const removeActionFromActionPlan = async (actionPlanId, action) => {
  const res = await api.put(`/action/actionPlans/actionPlanId/${actionPlanId}`, action);
  return res.data;
};

export const fetchActionPlansByAction = async (actionId) => {
  const res = await api.get(`/actionPlans/action/${actionId}`);
  return res.data;
};

// =====================================================
// ── New Design Section Entities ──
// =====================================================

// ── Company (企業情報・経営理念) ──
export const fetchCompany = async (companyId) => {
  const res = await api.get(`/companies/${companyId}`);
  return res.data;
};

export const saveCompanyValues = async (companyId, companyValues) => {
  const res = await api.post("/companies/companyValues", { companyId, companyValues });
  return res.data;
};

// ── Business KPI (事業指標) ──
export const fetchBusinessKpis = async (businessDomainId) => {
  const res = await api.get(`/businessKpis/businessDomain/${businessDomainId}`);
  return res.data;
};

export const fetchBusinessKpi = async (id) => {
  const res = await api.get(`/businessKpis/${id}`);
  return res.data;
};

export const createBusinessKpi = async (data) => {
  const res = await api.post("/businessKpis", data);
  return res.data;
};

export const updateBusinessKpi = async (data) => {
  const res = await api.put("/businessKpis", data);
  return res.data;
};

export const deleteBusinessKpi = async (id) => {
  const res = await api.delete(`/businessKpis/${id}`);
  return res.data;
};

// ── Data Domain (データドメイン) ──
export const fetchDataDomains = async (companyId) => {
  const res = await api.get(`/dataDomains/company/${companyId}`);
  return res.data;
};

export const fetchDataDomain = async (id) => {
  const res = await api.get(`/dataDomains/${id}`);
  return res.data;
};

export const createDataDomain = async (data) => {
  const res = await api.post("/dataDomains", data);
  return res.data;
};

export const updateDataDomain = async (data) => {
  const res = await api.put("/dataDomains", data);
  return res.data;
};

export const deleteDataDomain = async (id) => {
  const res = await api.delete(`/dataDomains/${id}`);
  return res.data;
};

// ── Strategy Map: Financial Goal (設計用) ──
export const fetchSmFinancialGoals = async (businessDomainId) => {
  const res = await api.get(`/financialGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Customer Value (設計用) ──
export const fetchSmCustomerValues = async (businessDomainId) => {
  const res = await api.get(`/customerValue/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Strategic Goal (設計用) ──
export const fetchSmStrategicGoals = async (businessDomainId) => {
  const res = await api.get(`/strategicGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Info Capital Goal App (設計用) ──
export const fetchSmInfoCapitalGoalsApp = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/app/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Info Capital Goal Data (設計用) ──
export const fetchSmInfoCapitalGoalsData = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/data/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Info Capital Goal IT (設計用) ──
export const fetchSmInfoCapitalGoalsIt = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/it/businessDomain/${businessDomainId}`);
  return res.data;
};

// ── Strategy Map: Org Capital Goal (設計用) ──
export const fetchSmOrgCapitalGoals = async (businessDomainId) => {
  const res = await api.get(`/organizationCapitalGoal/businessDomain/${businessDomainId}`);
  return res.data;
};

// =====================================================
// ── New Strategy Section Entities ──
// =====================================================

// ── Business Unit (事業単位) ──
export const fetchBusinessUnits = async (companyId) => {
  const res = await api.get(`/businessUnits/company/${companyId}`);
  return res.data;
};

export const fetchBusinessUnitsByDomain = async (businessDomainId) => {
  const res = await api.get(`/businessUnits/business_domain/${businessDomainId}`);
  return res.data;
};

export const fetchBusinessUnit = async (id) => {
  const res = await api.get(`/businessUnits/${id}`);
  return res.data;
};

export const createBusinessUnit = async (data) => {
  const res = await api.post("/businessUnits", data);
  return res.data;
};

export const updateBusinessUnit = async (data) => {
  const res = await api.put("/businessUnits", data);
  return res.data;
};

export const deleteBusinessUnit = async (id) => {
  const res = await api.delete(`/businessUnits/${id}`);
  return res.data;
};

// ── Project (プロジェクト) ──
export const fetchProjects = async (companyId) => {
  const res = await api.get(`/project/company/${companyId}`);
  return res.data;
};

export const fetchProjectsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/project/business_unit/${businessUnitId}`);
  return res.data;
};

export const fetchProject = async (id) => {
  const res = await api.get(`/projects/${id}`);
  return res.data;
};

export const createProject = async (data) => {
  const res = await api.post("/projects", data);
  return res.data;
};

export const updateProject = async (data) => {
  const res = await api.put("/projects", data);
  return res.data;
};

export const deleteProject = async (id) => {
  const res = await api.delete(`/projects/${id}`);
  return res.data;
};

// ── Project Workers / Members ──
export const fetchWorkersByProject = async (projectId) => {
  const res = await api.get(`/workers/project/${projectId}`);
  return res.data;
};

export const fetchMembersByProject = async (projectId) => {
  const res = await api.get(`/members/project/${projectId}`);
  return res.data;
};

export const fetchAssignableMembersForProject = async (projectId) => {
  const res = await api.get(`/assignable_members/project/${projectId}`);
  return res.data;
};

export const releaseMemberFromProject = async (memberId) => {
  const res = await api.delete(`/members/project/release/${memberId}`);
  return res.data;
};

export const releaseWorker = async (workerId) => {
  const res = await api.delete(`/workers/${workerId}/release`);
  return res.data;
};

export const assignMemberToProject = async (projectId, data) => {
  const res = await api.put(`/assign/member/project/${projectId}`, data);
  return res.data;
};

export const assignNewWorkerToProject = async (projectId, companyId, data) => {
  const res = await api.post(`/assign/member/project/${projectId}/company/${companyId}/worker`, data);
  return res.data;
};

// ── Program (プログラム) ──
export const fetchPrograms = async (companyId) => {
  const res = await api.get(`/program/company/${companyId}`);
  return res.data;
};

export const fetchProgramsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/program/business_unit/${businessUnitId}`);
  return res.data;
};

export const fetchProgram = async (id) => {
  const res = await api.get(`/programs/${id}`);
  return res.data;
};

export const createProgram = async (data) => {
  const res = await api.post("/programs", data);
  return res.data;
};

export const updateProgram = async (data) => {
  const res = await api.put("/programs", data);
  return res.data;
};

export const deleteProgram = async (id) => {
  const res = await api.delete(`/programs/${id}`);
  return res.data;
};

export const fetchWorkersByProgram = async (programId) => {
  const res = await api.get(`/workers/program/${programId}`);
  return res.data;
};

// ── Program Member ──
export const fetchMembersByProgram = async (programId) => {
  const res = await api.get(`/members/program/${programId}`);
  return res.data;
};
export const fetchAssignableMembersForProgram = async (programId) => {
  const res = await api.get(`/assignable_members/program/${programId}`);
  return res.data;
};
export const releaseMemberFromProgram = async (memberId) => {
  const res = await api.delete(`/members/program/release/${memberId}`);
  return res.data;
};
export const assignMemberToProgram = async (programId, data) => {
  const res = await api.put(`/assign/member/program/${programId}`, data);
  return res.data;
};
export const assignNewWorkerToProgram = async (programId, companyId, data) => {
  const res = await api.post(`/assign/member/program/${programId}/company/${companyId}/worker`, data);
  return res.data;
};

// ── Category fetch by businessUnit (for Project association) ──
export const fetchMemberCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/memberCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchPartnerCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/partnerCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchApplicationCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/applicationCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchPlaceCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/placeCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchFinancialAssetCategoriesByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/financialAssetCategories/businessUnit/${businessUnitId}`);
  return res.data;
};

// ── Project Category Associations ──
export const addProjectCustomerCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/customerCategory/${categoryId}`);
  return res.data;
};
export const removeProjectCustomerCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/customerCategory/${categoryId}`);
  return res.data;
};
export const addProjectProductCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/productCategory/${categoryId}`);
  return res.data;
};
export const removeProjectProductCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/productCategory/${categoryId}`);
  return res.data;
};
export const addProjectMemberCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/memberCategory/${categoryId}`);
  return res.data;
};
export const removeProjectMemberCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/memberCategory/${categoryId}`);
  return res.data;
};
export const addProjectPartnerCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/partnerCategory/${categoryId}`);
  return res.data;
};
export const removeProjectPartnerCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/partnerCategory/${categoryId}`);
  return res.data;
};
export const addProjectPlaceCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/placeCategory/${categoryId}`);
  return res.data;
};
export const removeProjectPlaceCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/placeCategory/${categoryId}`);
  return res.data;
};
export const addProjectDataCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/dataCategory/${categoryId}`);
  return res.data;
};
export const removeProjectDataCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/dataCategory/${categoryId}`);
  return res.data;
};
export const addProjectApplicationCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/applicationCategory/${categoryId}`);
  return res.data;
};
export const removeProjectApplicationCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/applicationCategory/${categoryId}`);
  return res.data;
};
export const addProjectFinancialAssetCategory = async (projectId, categoryId) => {
  const res = await api.post(`/projects/${projectId}/financialAssetCategory/${categoryId}`);
  return res.data;
};
export const removeProjectFinancialAssetCategory = async (projectId, categoryId) => {
  const res = await api.delete(`/projects/${projectId}/financialAssetCategory/${categoryId}`);
  return res.data;
};

// ── Project ActionPlan Association ──
export const addProjectActionPlan = async (projectId, actionPlanId) => {
  const res = await api.post(`/projects/${projectId}/actionPlan/${actionPlanId}`);
  return res.data;
};
export const removeProjectActionPlan = async (projectId, actionPlanId) => {
  const res = await api.delete(`/projects/${projectId}/actionPlan/${actionPlanId}`);
  return res.data;
};

// ── Project Goal (KPI) ──
export const fetchSuccessFactorsByBusinessDomain = async (businessDomainId) => {
  const res = await api.get(`/successFactor/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchProjectIndicators = async (projectId, indicatorType) => {
  const res = await api.get(`/project/indicator/${projectId}/indicatorType/${indicatorType}`);
  return res.data;
};
export const createProjectGoal = async (projectId, successFactorId, data) => {
  const res = await api.post(`/projects/${projectId}/goal/successFactor/${successFactorId}`, data);
  return res.data;
};
export const updateProjectGoal = async (projectId, data) => {
  const res = await api.put(`/projects/${projectId}/goal`, data);
  return res.data;
};
export const deleteProjectGoal = async (projectId, data) => {
  const res = await api.delete(`/projects/${projectId}/goal`, { data });
  return res.data;
};

// ── Project Achievement ──
export const createProjectAchievement = async (projectId, data) => {
  const res = await api.post(`/projects/${projectId}/achievement`, data);
  return res.data;
};
export const updateProjectAchievement = async (projectId, data) => {
  const res = await api.put(`/projects/${projectId}/achievement`, data);
  return res.data;
};
export const deleteProjectAchievement = async (projectId, data) => {
  const res = await api.delete(`/projects/${projectId}/achievement`, { data });
  return res.data;
};

// ── Program Category Associations ──
export const addProgramCustomerCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/customerCategory/${categoryId}`);
  return res.data;
};
export const removeProgramCustomerCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/customerCategory/${categoryId}`);
  return res.data;
};
export const addProgramProductCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/productCategory/${categoryId}`);
  return res.data;
};
export const removeProgramProductCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/productCategory/${categoryId}`);
  return res.data;
};
export const addProgramMemberCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/memberCategory/${categoryId}`);
  return res.data;
};
export const removeProgramMemberCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/memberCategory/${categoryId}`);
  return res.data;
};
export const addProgramPartnerCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/partnerCategory/${categoryId}`);
  return res.data;
};
export const removeProgramPartnerCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/partnerCategory/${categoryId}`);
  return res.data;
};
export const addProgramPlaceCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/placeCategory/${categoryId}`);
  return res.data;
};
export const removeProgramPlaceCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/placeCategory/${categoryId}`);
  return res.data;
};
export const addProgramDataCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/dataCategory/${categoryId}`);
  return res.data;
};
export const removeProgramDataCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/dataCategory/${categoryId}`);
  return res.data;
};
export const addProgramApplicationCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/applicationCategory/${categoryId}`);
  return res.data;
};
export const removeProgramApplicationCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/applicationCategory/${categoryId}`);
  return res.data;
};
export const addProgramFinancialAssetCategory = async (programId, categoryId) => {
  const res = await api.post(`/programs/${programId}/financialAssetCategory/${categoryId}`);
  return res.data;
};
export const removeProgramFinancialAssetCategory = async (programId, categoryId) => {
  const res = await api.delete(`/programs/${programId}/financialAssetCategory/${categoryId}`);
  return res.data;
};

// ── Program ActionPlan Association ──
export const addProgramActionPlan = async (programId, actionPlanId) => {
  const res = await api.post(`/programs/${programId}/actionPlan/${actionPlanId}`);
  return res.data;
};
export const removeProgramActionPlan = async (programId, actionPlanId) => {
  const res = await api.delete(`/programs/${programId}/actionPlan/${actionPlanId}`);
  return res.data;
};

// ── Program Goal (KPI) ──
export const createProgramGoal = async (programId, successFactorId, data) => {
  const res = await api.post(`/programs/${programId}/goal/successFactor/${successFactorId}`, data);
  return res.data;
};
export const updateProgramGoal = async (programId, data) => {
  const res = await api.put(`/programs/${programId}/goal`, data);
  return res.data;
};
export const deleteProgramGoal = async (programId, data) => {
  const res = await api.delete(`/programs/${programId}/goal`, { data });
  return res.data;
};

// ── Program Achievement ──
export const createProgramAchievement = async (programId, data) => {
  const res = await api.post(`/programs/${programId}/achievement`, data);
  return res.data;
};
export const updateProgramAchievement = async (programId, data) => {
  const res = await api.put(`/programs/${programId}/achievement`, data);
  return res.data;
};
export const deleteProgramAchievement = async (programId, data) => {
  const res = await api.delete(`/programs/${programId}/achievement`, { data });
  return res.data;
};

// ── Auth ──
export const signIn = async (data) => {
  const res = await api.post("/signIn", data);
  return res.data; // { result: "OK", returnValue: companyId }
};
export const checkCompany = async (data) => {
  const res = await api.post("/checkCompany", data);
  return res.data;
};
export const signUp = async (data) => {
  const res = await api.post("/signUp", data);
  return res.data;
};

// ── DataItems ──
export const fetchDataItemsByDataType = async (dataTypeId) => {
  const res = await api.get(`/dataItems/dataType/${dataTypeId}`);
  return res.data;
};
export const fetchDataItem = async (dataItemId) => {
  const res = await api.get(`/dataItems/${dataItemId}`);
  return res.data;
};
export const createDataItem = async (data) => {
  const res = await api.post("/dataItems", data);
  return res.data;
};
export const updateDataItem = async (data) => {
  const res = await api.put("/dataItems", data);
  return res.data;
};
export const deleteDataItem = async (dataItemId) => {
  const res = await api.delete(`/dataItems/${dataItemId}`);
  return res.data;
};

// ── BSC Goal ──
export const fetchBscFinancialGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/financialGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscCustomerValueGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/customerValue/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscStrategicGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/strategicGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscStrategicIssueGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/strategicIssue/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscHumanCapitalGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/humanCapitalGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalAppGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalApp/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalDataGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalData/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalItGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalIt/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscOrgCapitalGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/organizationCapitalGoal/goal/${businessUnitId}`);
  return res.data;
};
export const createBscGoal = async (businessUnitId, goal) => {
  const res = await api.post(`/businessUnit/successFactor/goal/${businessUnitId}`, goal);
  return res.data;
};
export const updateBscGoal = async (businessUnitId, goal) => {
  const res = await api.put(`/businessUnit/successFactor/goal/${businessUnitId}`, goal);
  return res.data;
};
export const deleteBscGoal = async (businessUnitId, goal) => {
  const res = await api.delete(`/businessUnit/successFactor/goal/${businessUnitId}`, { data: goal });
  return res.data;
};
export const fetchBscAchievements = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/achievement/${businessUnitId}`);
  return res.data;
};
